'use client'

import type { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { FC, useEffect, useState } from 'react'
import { type InteractiveButton, type PlayerConfig } from '../../../types/interactiveVideo'
import { useShadowHostPortal } from '../shadow-host-portal/ShadowHostPortalContext'
import BoxEntryBanner from './BoxEntryBanner'
import BoxModal from './BoxModal'

export type BoxPlayerProps = {
  scenarioId: string
  interactiveConfig: PlayerConfig
  nodes: ScenarioNodeWithVideoUrl[]
  isPlayerOpen: boolean
  entryThumbnailUrl: string | null
  entryThumbnailMimeType: string | null
  entryTitle: string
  entryCtaText: string
  onThumbnailClick: () => void
  onClosePlayer: () => void
  onVideoEnd: (nodeId: string) => void
  onVideoChange: (nodeId: string) => void
  onButtonClick: (button: InteractiveButton) => void
}

/**
 * BOX 型のシェル。ページ内の入口バナー ⇄ 全画面モーダルを切り替えるだけの薄い層で、
 * 分岐・アンケート・ルーティングは `InteractiveVideoPlayer` がそのまま担う。
 *
 * モーダルを開いている間は shadowHost を body 直下へ退避させる（SLIDER/TILE と同じ）。
 * インライン配置のままだと、埋め込み先の `overflow: hidden` や `transform` を持つ祖先に
 * 閉じ込められて全画面オーバーレイが成立しないため。
 */
const BoxPlayer: FC<BoxPlayerProps> = ({
  scenarioId,
  interactiveConfig,
  nodes,
  isPlayerOpen,
  entryThumbnailUrl,
  entryThumbnailMimeType,
  entryTitle,
  entryCtaText,
  onThumbnailClick,
  onClosePlayer,
  onVideoEnd,
  onVideoChange,
  onButtonClick,
}) => {
  const portal = useShadowHostPortal()
  const [currentNodeKey, setCurrentNodeKey] = useState(interactiveConfig.initialNodeId)
  // 「最初からやり直す」用。key を進めてプレイヤーを初期状態で組み直す

  useEffect(() => {
    portal?.setPortalActive(isPlayerOpen)
  }, [isPlayerOpen, portal])

  // 閉じたら次に開いたとき最初のノードから始める
  useEffect(() => {
    if (!isPlayerOpen) {
      setCurrentNodeKey(interactiveConfig.initialNodeId)
    }
  }, [isPlayerOpen, interactiveConfig.initialNodeId])

  const handleVideoChange = (nodeId: string) => {
    setCurrentNodeKey(nodeId)
    if (interactiveConfig.nodes.get(nodeId)?.type !== 'SURVEY') {
    }
    onVideoChange(nodeId)
  }

  return (
    <>
      {/* 入口バナーはページのフローに残したまま、モーダルを上に重ねる。
          アンマウントすると閉じたときにページの高さが跳ねる（レイアウトシフト）。 */}
      <BoxEntryBanner
        thumbnailUrl={entryThumbnailUrl}
        thumbnailMimeType={entryThumbnailMimeType}
        title={entryTitle}
        ctaText={entryCtaText}
        brandColor={interactiveConfig.defaultButtonColor}
        isHidden={isPlayerOpen}
        onClick={onThumbnailClick}
      />

      {isPlayerOpen && (
        <BoxModal
          scenarioId={scenarioId}
          config={interactiveConfig}
          nodes={nodes}
          currentNodeKey={currentNodeKey}
          onClose={onClosePlayer}
          onVideoEnd={onVideoEnd}
          onVideoChange={handleVideoChange}
          onButtonClick={onButtonClick}
        />
      )}
    </>
  )
}

export default BoxPlayer
